import { useMemo } from 'react';
import { Platform } from 'react-native';

import { useResponsive } from '@sg/garnish';

import { Navbar } from '../../../../../components';
import { DefaultHeaderLeft } from '../../../../components';
import {
  CONTENT_WITH_BG_COLOR_STYLES,
  CONTENT_WITHOUT_BG_STYLES,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptions,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Screen options for the modals stack.
 */
export const useModalStackOptions = () => {
  const { match } = useResponsive();

  const titles = useRouteTitles();

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(() => {
    return {
      ...SCREEN_HEADER_OPTIONS,
      headerShown: match([false, true]),
      header: match([undefined, () => <Navbar />]),
      headerLeft: () => <DefaultHeaderLeft />,
      presentation: match(['card', 'transparentModal']),
      contentStyle: CONTENT_WITH_BG_COLOR_STYLES,
    } satisfies ScreenOptions;
  }, [match]);

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Screen Options ───────────────────────────────────────

      Bag: { headerShown: false, customDocumentTitle: titles.bagCustomDocumentTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Reorder:
        { headerShown: false, title: titles.reorderTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      ReorderSubScreen:
        { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      RequestData: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      DeliveryOrderInFlight: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Feedback:
        { headerShown: match([true, false]), title: titles.rateOrderTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      OrderProduct: { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Chat: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES },
      PersonalData: { title: titles.personalDataTitle },
      AddGiftCard: { title: titles.addGiftCardTitle, headerShown: true },
      RedeemGiftCard: { title: titles.redeemGiftCardTitle, headerShown: true },
      Upsell: { contentStyle: CONTENT_WITHOUT_BG_STYLES, headerShown: false },
      Payments: {
        presentation: Platform.select({
          ios: 'modal',
          default: 'transparentModal',
        }),
        headerShown: false,
        title: titles.paymentsTitle,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      OrderStatus: {
        headerShown: true,
        customDocumentTitle: titles.orderStatusCustomDocumentTitle,
      },
      ForgotToScan: {
        customDocumentTitle: titles.forgotToScanCustomDocumentTitle,
      },
      GroupOrderSetup: {
        headerShown: false,
        title: titles.groupOrderSetupTitle,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      GroupOrderSearchLocation: {
        headerShown: false,
        title: titles.groupOrderLocationSearchTitle,
        contentStyle: CONTENT_WITHOUT_BG_STYLES,
      },
      Receipt: {
        headerShown: true,
        customDocumentTitle: titles.receiptCustomDocumentTitle,
      },
      ClaimReceipt: {
        headerShown: true,
        customDocumentTitle: titles.receiptClaimCustomDocumentTitle,
      },
      Credit: { headerShown: true, title: titles.creditAndPromoCodesTitle },
      CreditAdd: { headerShown: true, title: titles.creditAddTitle },
      CaDataRequests: { headerShown: true, title: titles.personalDataTitle },
      DietaryRestrictions: { headerShown: true, title: titles.dietaryRestrictionsTitle }, // prettier-ignore
      WithHeader: { headerShown: true },
    } satisfies ScreenOptionsMap;
  }, [
    match,
    stackOptions,
    titles.addGiftCardTitle,
    titles.bagCustomDocumentTitle,
    titles.creditAddTitle,
    titles.creditAndPromoCodesTitle,
    titles.dietaryRestrictionsTitle,
    titles.forgotToScanCustomDocumentTitle,
    titles.groupOrderLocationSearchTitle,
    titles.groupOrderSetupTitle,
    titles.orderStatusCustomDocumentTitle,
    titles.paymentsTitle,
    titles.personalDataTitle,
    titles.rateOrderTitle,
    titles.redeemGiftCardTitle,
    titles.reorderTitle,
    titles.receiptCustomDocumentTitle,
    titles.receiptClaimCustomDocumentTitle,
  ]);
};
